import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Network,
  Database,
  Sparkles,
  Globe2,
  TrendingUp,
  Briefcase,
  ShieldCheck,
  Compass,
  Filter,
  Send,
  RefreshCcw,
  CheckCircle2,
  ArrowDown,
} from "lucide-react";
import HeroDashboard from "../components/HeroDashboard";
import { BRAND } from "../lib/brand";

const SERVICES = [
  { icon: Network, title: "Affiliate Partner Network", desc: "Access to a vetted network of premium affiliates and publishers with proven reach across iGaming and sports betting audiences." },
  { icon: Database, title: "Proprietary Data & Insights", desc: "Large-scale first-party data sets that inform targeting, creative strategy and market positioning across key GEOs." },
  { icon: Sparkles, title: "AI-Powered Targeting", desc: "An AI stack that continuously analyses performance signals to optimise audience segments, creative delivery and spend allocation." },
  { icon: Globe2, title: "GEO-Focused Campaigns", desc: "Market-specific strategies designed to meet regulatory requirements and cultural nuances across priority global territories." },
  { icon: TrendingUp, title: "Revenue & Brand Growth", desc: "End-to-end campaign management focused on measurable player acquisition, retention uplift and brand visibility outcomes." },
  { icon: Briefcase, title: "C-Level Consultancy", desc: "Senior strategic guidance from operators and executives who have built and scaled affiliate programmes in competitive markets." },
];

const STEPS = [
  { n: "01", icon: Compass, title: "Discover", desc: "Understand the brand, the markets, the targets and the current acquisition challenge." },
  { n: "02", icon: Filter, title: "Segment", desc: "Define audiences, GEOs and compliant campaign routes around documented data." },
  { n: "03", icon: Send, title: "Activate", desc: "Launch campaigns across approved channels and the partner network with full sender control." },
  { n: "04", icon: RefreshCcw, title: "Optimise", desc: "Iterate using performance, suppression and partner feedback loops to grow what works." },
];

const TRUST = [
  { k: "01", t: "Data-led acquisition" },
  { k: "02", t: "Affiliate & partner network" },
  { k: "03", t: "Email & SMS campaign delivery" },
  { k: "04", t: "Compliance focused operations" },
];

const MARKETS = ["United Kingdom", "Germany", "Spain", "Italy", "Canada", "Brazil", "Mexico", "Japan", "Australia", "South Africa", "Netherlands", "Sweden", "Denmark", "Portugal", "Ireland"];

export default function Home() {
  React.useEffect(() => {
    document.title = "PlayerReach | Data Led Acquisition for iGaming and Entertainment Brands";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "PlayerReach is a performance marketing, customer acquisition and data company built for iGaming, sports betting and entertainment brands.");
  }, []);

  return (
    <div data-testid="home-page">
      {/* ===================== HERO ===================== */}
      <section className="relative bg-[#0A0E1A] text-white overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div aria-hidden className="absolute inset-0 hero-beam pointer-events-none" />
        <div aria-hidden className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />

        {/* Editorial top-rail */}
        <div className="relative container-pr pt-6 pb-2 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-white/45 font-mono-pr">
          <div className="flex items-center gap-3">
            <span className="pulse-dot" />
            <span>Live · operations</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <span>Iss. {new Date().getFullYear()}</span>
            <span>UAE · Sharjah</span>
            <span>B2B only</span>
          </div>
        </div>

        <div className="relative container-pr pt-10 pb-24 lg:pt-16 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <div className="lg:col-span-7 reveal-up">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.04] text-[11px] uppercase tracking-[0.2em] text-slate-300">
                <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
                iGaming · Sports betting · Entertainment
              </div>

              <h1 className="font-display mt-7 text-[44px] sm:text-6xl lg:text-[88px] font-medium leading-[0.95] tracking-[-0.04em]">
                <span className="block">Performance</span>
                <span className="block">marketing,</span>
                <span className="block">
                  built for{" "}
                  <span className="font-serif-edit italic font-normal text-cyan-300">player&nbsp;acquisition</span>
                  <span className="text-cyan-300">.</span>
                </span>
              </h1>

              <p className="mt-9 text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl reveal-up d-2">
                {BRAND.name} helps iGaming, sports betting and entertainment businesses acquire new
                players, grow brand visibility and unlock measurable revenue — through affiliate
                partnerships, data led audience strategy, compliant marketing delivery and AI
                powered optimisation.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3 reveal-up d-3">
                <Link to="/contact" className="btn-primary" data-testid="hero-cta-start">
                  Start a Conversation <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/services" className="btn-ghost-dark" data-testid="hero-cta-services">
                  Explore Services <ArrowDown className="h-4 w-4" />
                </Link>
              </div>

              {/* Editorial pillar row */}
              <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-0 reveal-up d-4">
                {TRUST.map((t, i) => (
                  <div key={t.k} className={`py-4 ${i !== 0 ? "sm:pl-5" : ""} ${i !== TRUST.length - 1 ? "sm:border-r sm:border-white/10 sm:pr-5" : ""}`}>
                    <div className="text-[10.5px] uppercase tracking-[0.22em] text-white/40 font-mono-pr">{t.k}</div>
                    <p className="mt-2 text-sm font-medium text-white leading-snug">{t.t}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 reveal-up d-2">
              <HeroDashboard />
            </div>
          </div>
        </div>

        {/* Marquee of markets */}
        <div className="relative border-t border-white/10 bg-black/30">
          <div className="container-pr py-4 marquee-mask overflow-hidden">
            <div className="pr-marquee-track text-sm text-white/50 font-display">
              {[...MARKETS, ...MARKETS].map((m, i) => (
                <span key={i} className="inline-flex items-center gap-3">
                  <span className="h-1 w-1 rounded-full bg-cyan-400/60" />
                  <span className="tracking-tight">{m}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================== EDITORIAL INTRO ===================== */}
      <section className="section-pad bg-white">
        <div className="container-pr">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="overline">§ 01 · What we do</p>
              <h2 className="font-display mt-5 text-4xl md:text-5xl lg:text-6xl font-medium text-slate-900 tracking-[-0.035em] leading-[1.04]">
                Acquisition systems for{" "}
                <span className="font-serif-edit italic font-normal text-slate-600">serious</span>
                {" "}operators and entertainment brands.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-lg lg:text-xl text-slate-600 leading-relaxed">
                {BRAND.name} operates at the intersection of affiliate marketing, customer
                acquisition, data, media and marketing technology. We support brands that need
                scalable acquisition, qualified audiences, performance based growth and accountable
                campaign delivery.
              </p>
              <p className="mt-6 text-base text-slate-500 leading-relaxed">
                Our team brings C-level experience across affiliate, media and technology — a
                curated network of premium publishing partners, a proprietary data asset, and
                AI-driven targeting to every engagement.
              </p>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500">
                <Stat label="Built around" value="B2B partnerships" />
                <Stat label="Reach across" value="Global GEOs" />
                <Stat label="Senior" value="C-suite team" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== SERVICES ===================== */}
      <section className="pb-24 lg:pb-32 bg-white">
        <div className="container-pr">
          <div className="flex items-end justify-between gap-6 mb-12">
            <div>
              <p className="overline">§ 02 · Services</p>
              <h2 className="font-display mt-5 text-4xl md:text-5xl font-medium text-slate-900 tracking-[-0.03em]">Six capabilities, one stack.</h2>
            </div>
            <Link to="/services" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-slate-900 border-b border-slate-900 hover:gap-2.5 transition-all" data-testid="services-link">
              All services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
            {SERVICES.map((s, i) => (
              <Link
                key={s.title}
                to="/services"
                className="group relative bg-white p-8 lg:p-10 transition-colors hover:bg-slate-50"
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-slate-50 border border-slate-200 text-[#0052FF] group-hover:bg-[#0052FF] group-hover:text-white group-hover:border-[#0052FF] transition-colors">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <span className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400 font-mono-pr">{String(i + 1).padStart(2, "0")} / 06</span>
                </div>
                <h3 className="font-display mt-7 text-xl lg:text-2xl font-medium text-slate-900 tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                <div className="mt-7 inline-flex items-center gap-1 text-sm font-medium text-slate-900">
                  <span className="border-b border-slate-900/0 group-hover:border-slate-900 transition-all pb-0.5">Learn more</span>
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== HOW IT WORKS ===================== */}
      <section className="section-pad bg-[#F6F8FB] border-y border-slate-200">
        <div className="container-pr">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <p className="overline">§ 03 · How it works</p>
              <h2 className="font-display mt-5 text-4xl md:text-5xl font-medium text-slate-900 tracking-[-0.03em] leading-[1.04]">
                From audience to revenue,{" "}
                <span className="font-serif-edit italic font-normal text-slate-600">in four deliberate steps.</span>
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed max-w-md">A repeatable operating model — designed so operators can see exactly where attention, data and capital are deployed.</p>
            </div>

            <div className="lg:col-span-7">
              <div className="relative">
                {/* connecting line on desktop */}
                <div aria-hidden className="hidden lg:block absolute left-7 top-0 bottom-0 w-px bg-slate-200" />
                <ol className="space-y-6">
                  {STEPS.map((s, i) => (
                    <li key={s.title} className="relative flex gap-5 lg:gap-7 items-start" data-testid={`step-${i}`}>
                      <div className="relative flex-shrink-0">
                        <span className="relative z-10 inline-flex h-14 w-14 items-center justify-center rounded-full bg-white border border-slate-200 text-slate-900 font-display text-base font-semibold shadow-sm">
                          {s.n}
                        </span>
                      </div>
                      <div className="flex-1 pt-2">
                        <div className="flex items-center gap-2.5">
                          <s.icon className="h-4 w-4 text-cyan-600" />
                          <h3 className="font-display text-lg font-semibold text-slate-900">{s.title}</h3>
                        </div>
                        <p className="mt-2 text-slate-600 leading-relaxed max-w-xl">{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== COMPLIANCE BAND ===================== */}
      <section className="section-pad bg-white">
        <div className="container-pr">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <p className="overline">§ 04 · Compliance</p>
              <h2 className="font-display mt-5 text-4xl md:text-5xl font-medium text-slate-900 tracking-[-0.03em] leading-[1.04]">
                Compliance{" "}
                <span className="font-serif-edit italic font-normal text-slate-600">is not an afterthought.</span>
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                {BRAND.name} operates with documented policies covering marketing communications,
                traffic sources, data usage, privacy, cookies and website terms. Our partner
                onboarding includes due diligence on data rights, consent records and traffic
                provenance.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/compliance" className="btn-primary" data-testid="home-compliance-cta">
                  Compliance Hub <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link to="/legal" className="btn-secondary" data-testid="home-legal-cta">
                  Legal Centre <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[
                  { icon: ShieldCheck, t: "Documented standards", d: "Marketing, traffic and data usage policies maintained as live governance." },
                  { icon: CheckCircle2, t: "Clear sender identification", d: "All direct marketing communications include recognisable sender and opt-out." },
                  { icon: Filter, t: "Suppression controls", d: "Centralised suppression applied before deployment and reapplied on cadence." },
                  { icon: ShieldCheck, t: "Partner due diligence", d: "Traffic sources, rights and consent records reviewed before activation." },
                ].map((c, idx) => (
                  <div key={c.t} className="card-flat p-6 lg:p-7 hover:-translate-y-0.5 hover:shadow-[0_18px_48px_-24px_rgba(15,23,42,0.18)]">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700">
                        <c.icon className="h-5 w-5" />
                      </span>
                      <span className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400 font-mono-pr">0{idx + 1}</span>
                    </div>
                    <p className="font-display mt-5 font-semibold text-slate-900 text-lg">{c.t}</p>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA ===================== */}
      <section className="pb-24 lg:pb-32 bg-white">
        <div className="container-pr">
          <div className="relative overflow-hidden rounded-3xl bg-[#0A0E1A] text-white p-10 lg:p-16 border border-white/5">
            <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
            <div aria-hidden className="absolute -top-40 -right-20 w-[520px] h-[520px] rounded-full bg-[#0052FF]/25 blur-3xl" />
            <div aria-hidden className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-cyan-500/15 blur-3xl" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <p className="overline-dark">§ Next step</p>
                <h2 className="font-display mt-5 text-4xl md:text-5xl lg:text-6xl font-medium tracking-[-0.03em] leading-[1.02]">
                  Let&apos;s build your next{" "}
                  <span className="font-serif-edit italic font-normal text-cyan-300">acquisition channel.</span>
                </h2>
                <p className="mt-6 text-base lg:text-lg text-slate-300/90 leading-relaxed max-w-2xl">
                  Whether you are entering a new market, scaling an existing programme, reviewing partner traffic, or exploring data led campaigns, {BRAND.name} can help structure the route from audience to revenue.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <div className="flex flex-wrap lg:justify-end gap-3">
                  <Link to="/contact" className="btn-primary" data-testid="final-cta-start">
                    Start a Conversation <ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <Link to="/partners" className="btn-ghost-dark" data-testid="final-cta-partner">
                    Become a Partner
                  </Link>
                </div>
                <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-white/40 font-mono-pr">{BRAND.contactEmail}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400 font-mono-pr">{label}</div>
      <div className="mt-1 text-slate-900 font-display font-medium">{value}</div>
    </div>
  );
}
